/**
 * Mobile push notifications (Web Push via Firebase Cloud Messaging).
 *
 * v1 scope: pushes mirror the user-facing emails that mean "something is
 * waiting for you" or "your request was decided". `sendEmail` looks up the
 * email's `kind` here; kinds not listed never push (reminders, digests,
 * reports and anything sent to external recipients stay email-only).
 *
 * Text is deliberately GENERIC — it shows on a locked phone, so no names,
 * amounts, dates or reasons. The tap opens the item (deep link taken from the
 * email) or, failing that, the tool's `route`.
 *
 * Device tokens live in /users/{email}/pushTokens/{id} (server-only).
 */
import { z } from 'zod';
export const PUSH_KINDS = {
    // Expense
    expense_submitted: { title: 'Expense statement to approve', route: '/expenses' },
    expense_forwarded: { title: 'Expense statement to approve', route: '/expenses' },
    expense_reassigned: { title: 'Expense statement to approve', route: '/expenses' },
    expense_approved: { title: 'Your expense statement was approved', route: '/expenses' },
    expense_rejected: { title: 'Your expense statement was returned', route: '/expenses' },
    // Invoice
    invoice_submitted: { title: 'Invoice to approve', route: '/invoices' },
    invoice_approved: { title: 'Your invoice was approved', route: '/invoices' },
    invoice_rejected: { title: 'Your invoice was returned', route: '/invoices' },
    // Credit card
    creditcard_submitted: { title: 'Credit card statement to approve', route: '/credit-cards' },
    creditcard_approved: { title: 'Your credit card statement was approved', route: '/credit-cards' },
    creditcard_rejected: { title: 'Your credit card statement needs changes', route: '/credit-cards' },
    creditcard_assigned: { title: 'Credit card statement to complete', route: '/credit-cards' },
    creditcard_receipt_request: { title: 'A receipt was requested from you', route: '/credit-cards' },
    // Absence
    absence_request: { title: 'Absence request to approve', route: '/absence' },
    absence_decision: { title: 'Your absence request was decided', route: '/absence' },
    // Time tracking
    time_submitted: { title: 'Time sheet to approve', route: '/time' },
    time_decision: { title: 'Your time sheet was decided', route: '/time' },
    // Signatures
    signature_request: { title: 'Document to sign', route: '/signatures' },
    signature_completed: { title: 'A document was fully signed', route: '/signatures' },
    signature_declined: { title: 'A signer declined your document', route: '/signatures' },
    // Performance reviews
    review_invite: { title: 'Review to complete', route: '/performance-reviews' },
    review_nomination_request: { title: 'Peer nominations requested', route: '/performance-reviews' },
    // Projects
    project_action_assigned: { title: 'Action item assigned to you', route: '/projects' },
    project_request_accepted: { title: 'Your project request was accepted', route: '/projects' },
    project_request_returned: { title: 'Your project request was returned', route: '/projects' },
    // Hub
    hub_access_request: { title: 'Access request to review', route: '/admin' },
    announcement: { title: 'New announcement on DAAily Hub', route: '/' },
};
/** Body line under every push — never carries item details. */
export const PUSH_BODY = 'Tap to open in DAAily Hub';
/**
 * Rollout gate: who may register a device for push (Profile card, Home prompt).
 * Server pushes go to any user with a registered device, so gating registration
 * gates the feature. `config.pushOpenToAll` (Config → Hub, Sep 2026) opens it to
 * every user (still opt-in per device); off = admins + hub admins (HR) only.
 */
export function canUsePush(email, isAdmin, config) {
    if (isAdmin || config?.pushOpenToAll === true)
        return true;
    const e = email.toLowerCase();
    return (config?.hubAdminEmails ?? []).some((a) => a.toLowerCase() === e);
}
export const PushRegisterSchema = z.object({
    token: z.string().min(20).max(4096),
    /** Short device label for the Profile list, e.g. "iPhone · Safari". */
    device: z.string().max(120).optional(),
});
export const PushUnregisterSchema = z.object({
    token: z.string().min(20).max(4096),
});
//# sourceMappingURL=push.js.map